import { useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

// CUAD clause category IDs supported by the gate engine
const CUAD_CATEGORIES = [
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'limitation_of_liability', label: 'Limitation of Liability' },
  { id: 'termination_for_convenience', label: 'Termination for Convenience' },
  { id: 'change_of_control', label: 'Change of Control' },
  { id: 'non_compete', label: 'Non-Compete' },
  { id: 'ip_ownership', label: 'IP Ownership' },
  { id: 'audit_rights', label: 'Audit Rights' },
  { id: 'governing_law', label: 'Governing Law' },
];

export interface GateConfigOverride {
  require_all_signatures: boolean;
  require_compliance_pass: boolean;
  human_review_on_reject: boolean;
  blocking_cuad_categories: string[];
  jurisdiction_whitelist: string[];
}

interface GateConfigPanelProps {
  value: GateConfigOverride;
  onChange: (next: GateConfigOverride) => void;
  className?: string;
}

export function GateConfigPanel({ value, onChange, className }: GateConfigPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => { setIsOpen((prev) => !prev); };

  const setField = <K extends keyof GateConfigOverride>(key: K, val: GateConfigOverride[K]) => {
    onChange({ ...value, [key]: val });
  };

  const toggleCategory = (id: string) => {
    const next = value.blocking_cuad_categories.includes(id)
      ? value.blocking_cuad_categories.filter((c) => c !== id)
      : [...value.blocking_cuad_categories, id];
    setField('blocking_cuad_categories', next);
  };

  const accordionId = 'gate-config-panel';
  const bodyId = 'gate-config-body';

  return (
    <div className={cn('rounded-lg border border-border', className)}>
      <button
        type="button"
        id={accordionId}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={toggle}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
      >
        <span>Gate Configuration Override</span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div id={bodyId} role="region" aria-labelledby={accordionId} className="space-y-5 border-t border-border px-4 py-4">
          <p className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            These settings override your client&apos;s default gate configuration for this job only.
            Per FR-077 v3.2, every gate outcome — including APPROVED — still routes to the human
            review queue; there is no threshold that bypasses human sign-off.
          </p>

          {/* Boolean toggles */}
          <fieldset className="space-y-3">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Gate Toggles
            </legend>
            {(
              [
                ['require_all_signatures', 'Require All Signatures'],
                ['require_compliance_pass', 'Require Compliance Pass'],
                ['human_review_on_reject', 'Human Review on Reject'],
              ] as const
            ).map(([key, label]) => {
              const fieldId = `gate-toggle-${key}`;
              return (
                <label key={key} htmlFor={fieldId} className="flex cursor-pointer items-center justify-between gap-3">
                  <span className="text-sm text-foreground">{label}</span>
                  <button
                    type="button"
                    role="switch"
                    id={fieldId}
                    aria-checked={value[key]}
                    onClick={() => { setField(key, !value[key]); }}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      value[key] ? 'bg-primary' : 'bg-input',
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-background shadow-md transition-transform',
                        value[key] ? 'translate-x-4' : 'translate-x-0',
                      )}
                      aria-hidden="true"
                    />
                  </button>
                </label>
              );
            })}
          </fieldset>

          {/* Blocking CUAD Categories */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Blocking CUAD Categories
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {CUAD_CATEGORIES.map(({ id, label }) => {
                const checked = value.blocking_cuad_categories.includes(id);
                const cbId = `cuad-${id}`;
                return (
                  <label key={id} htmlFor={cbId} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      id={cbId}
                      checked={checked}
                      onChange={() => { toggleCategory(id); }}
                      className="h-4 w-4 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
}
